import { resolveApiBaseUrl } from '../utils/apiBase';
import { ensureCsrfCookie, xsrfHeaders } from './authApi';

const API = () => resolveApiBaseUrl();

async function jsonFetch(path, options = {}) {
  await ensureCsrfCookie();
  const response = await fetch(`${API()}${path}`, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...xsrfHeaders(),
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
    ...options,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Request failed (${response.status})`);
  }
  if (response.status === 204) return null;
  return response.json();
}

export async function listPublicAgents() {
  return jsonFetch('/v1/agents?visibility=public&status=published');
}

export async function listUserAgents() {
  return jsonFetch('/v1/user/agents');
}

export async function createUserAgent(payload) {
  return jsonFetch('/v1/user/agents', { method: 'POST', body: JSON.stringify(payload) });
}

export async function listProjects() {
  return jsonFetch('/v1/user/projects');
}

export async function getProject(projectId) {
  return jsonFetch(`/v1/user/projects/${projectId}`);
}

export async function getProjectChatHistory(projectId, agentSlug = 'research') {
  const q = agentSlug ? `?agentSlug=${encodeURIComponent(agentSlug)}` : '';
  return jsonFetch(`/v1/user/projects/${projectId}/chat-history${q}`);
}

export async function createProject(payload) {
  return jsonFetch('/v1/user/projects', { method: 'POST', body: JSON.stringify(payload) });
}

export async function getProjectWhatsNew(projectId) {
  return jsonFetch(`/v1/user/projects/${projectId}/whats-new`);
}

export async function refreshProject(projectId) {
  return jsonFetch(`/v1/user/projects/${projectId}/refresh`, { method: 'POST' });
}

export async function listMaterials(projectId) {
  const q = projectId ? `?projectId=${projectId}` : '';
  return jsonFetch(`/v1/user/materials${q}`);
}

export async function ingestWikipedia(url, title, projectId) {
  const q = projectId ? `?projectId=${projectId}` : '';
  return jsonFetch(`/v1/user/materials/wikipedia${q}`, {
    method: 'POST',
    body: JSON.stringify({ url, title }),
  });
}

export async function uploadMaterial(file, title, projectId) {
  await ensureCsrfCookie();
  const form = new FormData();
  form.append('file', file);
  if (title) form.append('title', title);
  const q = projectId ? `?projectId=${projectId}` : '';
  const response = await fetch(`${API()}/v1/user/materials${q}`, {
    method: 'POST',
    credentials: 'include',
    headers: xsrfHeaders(),
    body: form,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Upload failed (${response.status})`);
  }
  return response.json();
}

export async function listAdminAgents() {
  return jsonFetch('/v1/admin/agents');
}

export async function publishAdminAgent(id) {
  return jsonFetch(`/v1/admin/agents/${id}/publish`, { method: 'POST' });
}

export async function updateAdminAgent(id, payload) {
  return jsonFetch(`/v1/admin/agents/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
}

export async function agentChat(slug, message, sessionId, researchProjectId) {
  return jsonFetch(`/v1/ai/agents/${slug}/chat`, {
    method: 'POST',
    body: JSON.stringify({
      message,
      ...(sessionId ? { sessionId } : {}),
      ...(researchProjectId ? { researchProjectId } : {}),
    }),
  });
}

export async function generateResearchDraft({
  query,
  sourceArticleIds = [],
  includeArticles = true,
  includeAnalystThread = false,
  includeMaterials = false,
  researchProjectId,
  researchSessionId,
  editorSessionId,
}) {
  return jsonFetch('/research/draft/generate', {
    method: 'POST',
    body: JSON.stringify({
      query,
      sourceArticleIds,
      includeArticles,
      includeAnalystThread,
      includeMaterials,
      ...(researchProjectId ? { researchProjectId } : {}),
      ...(researchSessionId ? { researchSessionId } : {}),
      ...(editorSessionId ? { editorSessionId } : {}),
    }),
  });
}

export async function createResearchDraft({
  title,
  content,
  summary,
  sourceArticleIds,
  researchQuery,
  researchProjectId,
  editorSessionId,
  includeArticles,
  includeAnalystThread,
  includeMaterials,
  includedChatTurnCount,
  promptVersion,
}) {
  return jsonFetch('/research/articles', {
    method: 'POST',
    body: JSON.stringify({
      title,
      content,
      summary,
      sourceArticleIds,
      ...(researchQuery ? { researchQuery } : {}),
      ...(researchProjectId ? { researchProjectId } : {}),
      ...(editorSessionId ? { editorSessionId } : {}),
      ...(includeArticles !== undefined ? { includeArticles } : {}),
      ...(includeAnalystThread !== undefined ? { includeAnalystThread } : {}),
      ...(includeMaterials !== undefined ? { includeMaterials } : {}),
      ...(includedChatTurnCount != null ? { includedChatTurnCount } : {}),
      ...(promptVersion ? { promptVersion } : {}),
    }),
  });
}

export async function listResearchArticles({ status, page = 0, size = 20 } = {}) {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (status) params.set('status', status);
  return jsonFetch(`/research/articles?${params}`);
}

export async function updateResearchDraft(articleId, { title, content, summary }) {
  return jsonFetch(`/research/articles/${articleId}`, {
    method: 'PUT',
    body: JSON.stringify({ title, content, summary }),
  });
}

export async function publishResearchArticle(articleId, { displayName, authorSlug } = {}) {
  const body =
    displayName || authorSlug
      ? JSON.stringify({
          ...(displayName ? { displayName } : {}),
          ...(authorSlug ? { authorSlug } : {}),
        })
      : undefined;
  return jsonFetch(`/research/articles/${articleId}/publish`, {
    method: 'POST',
    ...(body ? { body } : {}),
  });
}
